/**
 * FORGE → MANIFEST hand-off.
 *
 * On award, assemble a Manifest "order" from the FORGE workspace summary
 * (finalized spec sheet + awarded suppliers/quotes + bid requirements) and
 * write it into Manifest's localStorage store, so MANIFEST picks up at
 * Contract & Pay with the relationship already warm. Manifest is a
 * localStorage POC, so the hand-off is client-side; the shared comms threads
 * are re-tagged forge→manifest by the backend (/forge/:id/mark-awarded).
 *
 * Note: FORGE supports mixed-supplier bids (a supplier per line item) but
 * Manifest's order model assumes a single primary factory — we carry all
 * awarded suppliers in `factories[]` and set the first as pickedQuoteFactoryId.
 */
import { SEED_ORDERS } from '../Manifest/fixtures';

const STORAGE_KEY = 'aiquire_manifest_v1';

export function buildManifestOrder(summary) {
  const sheet = summary?.spec_sheet || {};
  const bid = summary?.bid_summary || {};
  const reqs = summary?.bid_requirements || {};
  const shortlist = summary?.shortlist || [];
  const quotesByLine = summary?.quotes?.line_items || [];
  const tenderId = summary?.tender_id;
  const now = new Date().toISOString();

  const awardedNames = new Set(bid.suppliers || []);
  const factories = shortlist
    .filter((c) => c.status === 'accepted')
    .map((c) => ({
      id: c.candidate_id,
      name: c.company,
      country: c.country || '',
      verified: true,
      yearsActive: c.stats?.years || null,
      moq: c.stats?.moq || null,
      leadTimeDays: null,
      contact: c.contacts?.email || '',
      listedPrice: null,
      status: awardedNames.has(c.company) ? 'awarded' : 'passed',
    }));

  const quotes = [];
  quotesByLine.forEach((li) => {
    const aw = (li.quotes || []).find((q) => q.awarded);
    if (aw) {
      quotes.push({
        factoryId: aw.supplier_id,
        unitPriceUsd: aw.unit_price,
        leadTimeDays: aw.lead_time_weeks ? aw.lead_time_weeks * 7 : null,
        incoterm: aw.terms || 'DDP',
        landedCostCadEach: aw.unit_price_cad,
        notes: `Line ${li.line_item}: ${li.product_name} — ${aw.factory}`,
      });
    }
  });
  const pickedQuoteFactoryId = quotes[0]?.factoryId || null;

  const sections = (sheet.line_items || []).map((li) => ({
    heading: `${li.product_name}${li.sku ? ` (${li.sku})` : ''}`,
    body: (li.technical_specifications || [])
      .map((t) => `${t.specification}: ${t.requirement}`).join('; ') || li.purpose || '',
  }));

  // FORGE completed Spec & Source + Quote & Negotiate (steps 1–6). Manifest
  // picks up at step 7 (supplier contract + pro forma).
  const stepStates = {};
  for (let i = 1; i <= 6; i += 1) {
    stepStates[i] = { stepId: i, status: 'done', notes: 'Completed in FORGE.', attachments: [], completedAt: now };
  }
  stepStates[7] = { stepId: 7, status: 'in_progress', notes: 'Awarded — request supplier contract + pro forma invoice.', attachments: [] };

  return {
    id: tenderId,
    label: sheet.title || tenderId,
    contractTitle: sheet.title || reqs.title || tenderId,
    buyer: reqs.buyer || (reqs.point_of_contact?.title || '').replace(/.*,\s*/, '') || 'Government of Canada',
    solicitationNumber: tenderId,
    awardDate: now,
    contractValueCad: bid.total_landed_cad || null,
    quantity: (sheet.line_items || []).reduce((s, li) => s + ((li.quote_quantities || []).slice(-1)[0] || 0), 0) || null,
    productSummary: (sheet.line_items || []).map((li) => li.product_name).join(', '),
    deliveryRegion: sheet.ddp_address || '',
    deliveryDeadline: reqs.closing_date || null,
    factories,
    quotes,
    pickedQuoteFactoryId,
    techSpec: { generated: true, lastUpdated: now, sections },
    stepStates,
    tracking: null,
    customs: null,
    fromForge: true,
  };
}

export function handoffToManifest(summary) {
  const order = buildManifestOrder(summary);
  let orders = null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    orders = raw ? JSON.parse(raw) : null;
  } catch (e) {
    orders = null;
  }
  if (!Array.isArray(orders) || orders.length === 0) {
    orders = JSON.parse(JSON.stringify(SEED_ORDERS));
  }
  const idx = orders.findIndex((o) => o.id === order.id);
  if (idx >= 0) orders[idx] = order;
  else orders.unshift(order);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(orders));
  return order.id;
}
